// ==================================================================================
// Digital Approval System (DAS) - Approval Actions Hooks
// ==================================================================================
// Custom hooks for approval action operations
// ==================================================================================

import { useState, useCallback, useEffect } from 'react';
import { approvalActionsService } from '../../../api/services/das/approvalActionsService';
import {
  ApproveRequestRequest,
  RejectRequestRequest,
  EscalateRequestRequest,
  RecommendRequestRequest,
  ReturnForClarificationRequest,
  ReturnForClarificationResponse,
  ResubmitRequestRequest,
  ResubmitRequestResponse,
  CancelRequestRequest,
  CancelRequestResponse,
  AddCommentRequest,
  EligibleReturnRecipient,
} from '../../../api/types/das/approvalActions.types';
import { Request } from '../../../api/types/requests.types';

/**
 * Hook for approval action operations
 */
export const useApprovalActions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Approve a request
   */
  const approve = async (id: string, request: ApproveRequestRequest): Promise<Request | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.approve(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reject a request
   */
  const reject = async (id: string, request: RejectRequestRequest): Promise<Request | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.reject(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Escalate a request
   */
  const escalate = async (id: string, request: EscalateRequestRequest): Promise<Request | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.escalate(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to escalate request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Recommend approval/rejection
   */
  const recommend = async (id: string, request: RecommendRequestRequest): Promise<Request | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.recommend(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to recommend';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get users eligible to receive a returned request
   * Returns the original requestor and any approvers who have already taken action
   */
  const getEligibleReturnRecipients = async (id: string): Promise<EligibleReturnRecipient[]> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.getEligibleReturnRecipients(id);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get eligible return recipients';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Return for clarification
   */
  const returnForClarification = async (
    id: string,
    request: ReturnForClarificationRequest
  ): Promise<ReturnForClarificationResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.returnForClarification(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to return for clarification';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resubmit a returned request
   */
  const resubmit = async (
    id: string,
    request: ResubmitRequestRequest
  ): Promise<ResubmitRequestResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.resubmit(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resubmit request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel a request (only by creator)
   */
  const cancel = async (
    id: string,
    request: CancelRequestRequest
  ): Promise<CancelRequestResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.cancel(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel request';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add a comment
   */
  const addComment = async (id: string, request: AddCommentRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.addComment(id, request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    approve,
    reject,
    escalate,
    recommend,
    getEligibleReturnRecipients,
    returnForClarification,
    resubmit,
    cancel,
    addComment,
  };
};

// ==================================================================================
// RETURNED REQUESTS HOOKS
// ==================================================================================

/**
 * Hook for fetching returned requests for the current user (as requestor)
 */
export const useMyReturnedRequests = (topN?: number) => {
  const [data, setData] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReturnedRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.getMyReturned(topN);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch returned requests';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [topN]);

  useEffect(() => {
    fetchReturnedRequests();
  }, [fetchReturnedRequests]);

  return {
    data,
    loading,
    error,
    refetch: fetchReturnedRequests,
  };
};

/**
 * Hook for fetching requests returned by the current user (as approver)
 */
export const useReturnedByMeRequests = (topN?: number) => {
  const [data, setData] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReturnedByMe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await approvalActionsService.getReturnedByMe(topN);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch returned requests';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [topN]);

  useEffect(() => {
    fetchReturnedByMe();
  }, [fetchReturnedByMe]);

  return {
    data,
    loading,
    error,
    refetch: fetchReturnedByMe,
  };
};
